// Believable Q&A history — what the user asked AI about each paper / globally.
// Used in the Detail page (per-paper history) and the dedicated Q&A History screen.

const QA_CATEGORIES = [
  { id: 'all',          ko: '전체',          en: 'All' },
  { id: 'novelty',      ko: 'Novelty',       en: 'Novelty' },
  { id: 'critique',     ko: '약점 · 한계',   en: 'Weaknesses' },
  { id: 'method',       ko: '방법 · 구현',   en: 'Method · Impl' },
  { id: 'compare',      ko: '비교',          en: 'Comparison' },
  { id: 'repro',        ko: '재현성',        en: 'Reproducibility' },
  { id: 'dataset',      ko: '데이터셋',      en: 'Dataset' },
  { id: 'idea',         ko: '아이디어',      en: 'Ideation' },
  { id: 'venue',        ko: '학회 · 제출',   en: 'Venue · Submission' },
];

const QUESTIONS = [
  {
    id: 'q1',
    paperId: 'p1', // GS-DiT
    cat: 'novelty',
    when: '2시간 전',
    whenEn: '2h ago',
    starred: true,
    q: 'GS-DiT의 진짜 contribution은 DiT prior인가, canonical-id cache인가?',
    qEn: 'Is the real contribution of GS-DiT the DiT prior, or the canonical-id cache?',
    a: '둘 중 하나면 DiT prior. Ablation Tbl. 4에서 cache만 추가하면 +0.1 PSNR / 3.1× speed, prior만 추가하면 +0.9 PSNR / 1.0× speed. 즉 품질 향상은 prior에서, 속도는 cache에서. Cache는 엔지니어링이고 prior가 진짜 아이디어.',
    aEn: 'Mostly the DiT prior. Tbl. 4 shows cache-only adds +0.1 PSNR at 3.1× speed; prior-only adds +0.9 PSNR at 1.0× speed. Quality from prior, speed from cache. Cache is engineering, prior is the actual idea.',
    sources: 2, // # of cited chunks
    follow: ['DiT prior 없이 cache만으로 갈 수는 없나?', '28k 클립 사전학습이 없으면 어떻게 되나?'],
  },
  {
    id: 'q2',
    paperId: 'p1',
    cat: 'critique',
    when: '2시간 전',
    whenEn: '2h ago',
    q: '이 논문이 NeurIPS reviewer라면 받을 가장 큰 비판은?',
    qEn: 'If reviewed at NeurIPS, what would be the strongest critique?',
    a: '세 가지 — (1) 28k 클립 사전학습이 비공개·재현 불가 → "open" 주장이 약함. (2) D-NeRF만 평가, HyperNeRF·NeRF-DS 같은 어려운 dataset 누락. (3) 4-bit cache가 lossless하다는 주장의 실증이 §3.3 fig 4에 국한됨 — quantitative limits 없음.',
    aEn: "Three: (1) the 28k-clip pretrain corpus is proprietary — undermines the 'open' framing. (2) Eval only on D-NeRF; HyperNeRF and NeRF-DS (harder) are missing. (3) Claim that the 4-bit cache is lossless is shown only in Fig. 4 §3.3 — no quantitative bound.",
    sources: 3,
    follow: ['Sparse-view에서 같은 트릭이 무너지는가?'],
  },
  {
    id: 'q3',
    paperId: 'p1',
    cat: 'method',
    when: '어제',
    whenEn: 'yesterday',
    q: 'Cache 코드북 크기 64는 어떻게 정해진 건가?',
    qEn: 'How was the codebook size of 64 chosen for the cache?',
    a: '§3.3에서 K=2⁴=16부터 K=2⁸=256까지 sweep. K≥64에서 PSNR 변화가 0.05 이하 — 즉 데이터-driven 선택. 4-bit이라는 표기는 misleading — 실제 인덱스는 6-bit지만 디스크 저장은 4-bit packed.',
    aEn: 'Swept K=16 → 256 in §3.3. PSNR delta stays below 0.05 for K≥64, so it is data-driven. "4-bit" label is misleading — actual index is 6-bit, but on-disk storage is 4-bit packed.',
    sources: 1,
    follow: [],
  },
  {
    id: 'q4',
    paperId: 'p1',
    cat: 'idea',
    when: '어제',
    whenEn: 'yesterday',
    starred: true,
    q: '랙 트윈 프로젝트에 GS-DiT를 적용하려면 무엇이 부족한가?',
    qEn: 'What is missing to apply GS-DiT to the rack-twin project?',
    a: '두 가지 갭 — (1) 사전학습 코퍼스: 랙은 형광등 + 금속 표면이 dominant인데 DiT prior는 일반 자연 씬으로 학습됨 → metallic BRDF에 일반화 가능성 미검증. (2) 입력 뷰 수: 우리는 4-cam, 논문은 8-view → §5.2 sparse-view ablation에서 8→4 시 -3.2 PSNR. 권장: prior를 우리 도메인에서 fine-tune + sparse-view 3DGS distillation 추가.',
    aEn: 'Two gaps — (1) pretrain corpus: rack is dominated by fluorescent lights + metallic surfaces, but the DiT prior is trained on generic natural scenes → no evidence it generalizes to metallic BRDFs. (2) view count: we have a 4-cam rig vs the paper\'s 8-view setup; §5.2 sparse-view ablation drops -3.2 PSNR going 8 → 4. Recommended: fine-tune the prior on our domain + add sparse-view 3DGS distillation.',
    sources: 4,
    follow: ['Fine-tuning prior에 필요한 최소 클립 수는?'],
  },
  {
    id: 'q5',
    paperId: 'p1',
    cat: 'compare',
    when: '3일 전',
    whenEn: '3d ago',
    q: 'GS-DiT vs 4DGS-Cache를 같은 해상도·같은 GPU에서 공정 비교하면?',
    qEn: 'How does GS-DiT compare to 4DGS-Cache at equal resolution and GPU?',
    a: '벤치 메모리에 정규화됨 — 800×800 / 4090에서 GS-DiT 84 FPS / 31.74 PSNR, 4DGS-Cache 72 FPS / 31.21 PSNR. 4DGS-Cache의 120 FPS 주장은 600×600. GS-DiT가 양쪽 지표 모두에서 우세.',
    aEn: 'Normalized in Bench Memory — at 800×800 / 4090, GS-DiT 84 FPS / 31.74 PSNR vs 4DGS-Cache 72 FPS / 31.21 PSNR. The 120 FPS in 4DGS-Cache uses 600×600. GS-DiT wins on both metrics.',
    sources: 2,
    follow: [],
  },
  {
    id: 'q6',
    paperId: 'p2', // OpenVLA-2
    cat: 'novelty',
    when: '어제',
    whenEn: 'yesterday',
    q: 'OpenVLA-2가 v1 대비 진짜 새로운 것은?',
    qEn: 'What is actually new in OpenVLA-2 vs v1?',
    a: '세 가지 — (1) 7B → 13B params, (2) 38개 임바디먼트 MoE 헤드 (이게 핵심 — §4.3에서 +9.1pt 기여로 명시), (3) Open-X 데이터셋 1.2M trajectories (v1: 250k). MoE 헤드가 진짜 contribution이고 나머지는 스케일링.',
    aEn: 'Three — (1) 7B → 13B params, (2) 38-embodiment MoE head (this is the core — §4.3 attributes +9.1pt to it), (3) Open-X with 1.2M trajectories (v1: 250k). The MoE head is the actual contribution; the rest is scaling.',
    sources: 3,
    follow: ['MoE 헤드를 7B에 backport하면 v1과 격차가 줄어드나?'],
  },
  {
    id: 'q7',
    paperId: 'p2',
    cat: 'repro',
    when: '2일 전',
    whenEn: '2d ago',
    q: '단일 GPU 환경에서 OpenVLA-2를 재현할 수 있나?',
    qEn: 'Can OpenVLA-2 be reproduced on a single GPU?',
    a: '학습은 불가능 (논문: 256 × A100, 11일). 추론은 가능 — 13B에서 bf16 시 26GB → A100 80GB 또는 H100 한 장. INT4 양자화 hf 체크포인트 공개됨 (12GB), RTX 4090 추론 가능. ALOHA real-robot은 latency 50ms 이내.',
    aEn: 'Training is not feasible (paper: 256 × A100, 11 days). Inference is — 13B bf16 needs 26GB → fits on one A100 80GB or H100. An INT4 hf checkpoint is released (12GB), runs on RTX 4090. ALOHA real-robot latency stays under 50ms.',
    sources: 2,
    follow: [],
  },
  {
    id: 'q8',
    paperId: null, // global question
    cat: 'venue',
    when: '4일 전',
    whenEn: '4d ago',
    starred: true,
    q: '얇은 금속 형상용 실시간 4DGS 결과를 어디에 내야 하나?',
    qEn: 'Where to submit a real-time 4DGS result for thin metallic geometry?',
    a: 'Three viable venues — (1) SIGGRAPH 26 (Jan 22 deadline): 시스템·렌더링 기여 강조 시 best fit; reviewers expect FPS·visual fidelity demo. (2) CVPR 27 (Nov deadline): novelty가 method-level이면 reasonable; SIGGRAPH보다 ablation 엄밀성 요구. (3) 3DV 26 (May): 작은 컨퍼런스지만 metallic / industrial 도메인에 특화된 reviewer가 많음 — 데이터셋 + 응용 contribution이면 ideal. 추천: SIGGRAPH가 1순위.',
    aEn: 'Three viable venues — (1) SIGGRAPH 26 (Jan 22 deadline): best fit if framed as systems + rendering; reviewers expect FPS + fidelity demo. (2) CVPR 27 (Nov deadline): reasonable if novelty is method-level; needs stricter ablations than SIGGRAPH. (3) 3DV 26 (May): smaller, but more reviewers familiar with metallic / industrial. Best recommendation: SIGGRAPH first.',
    sources: 0,
    follow: ['SIGGRAPH 26에 맞춰 어떤 ablation을 미리 준비해야 하나?'],
  },
  {
    id: 'q9',
    paperId: 'p4', // 4DGS-Cache
    cat: 'critique',
    when: '5일 전',
    whenEn: '5d ago',
    q: '4DGS-Cache가 120 Hz를 뽑은 핵심은 무엇이고 약점은?',
    qEn: 'What is behind the 120 Hz claim in 4DGS-Cache, and what is the weakness?',
    a: '핵심: per-Gaussian 변위를 4D triplane으로 baking. 약점 두 가지 — (1) 해상도 600×600에서만 측정, 800×800 표준으로 정규화하면 72 FPS. (2) Triplane 메모리 800MB+ → mobile GPU 불가. 산업 응용에는 GS-DiT의 4-bit cache가 더 실용적.',
    aEn: 'Key: bakes per-Gaussian displacement into a 4D triplane. Two weaknesses — (1) only measured at 600×600; renormalized to 800×800 it is 72 FPS. (2) Triplane memory is 800MB+ → infeasible on mobile GPU. For industrial deployment, GS-DiT\'s 4-bit cache is more practical.',
    sources: 2,
    follow: [],
  },
  {
    id: 'q10',
    paperId: null,
    cat: 'idea',
    when: '1주 전',
    whenEn: '1w ago',
    q: 'GS-DiT + 4DGS-Cache의 강점을 합친 contribution 후보는?',
    qEn: 'What contribution could combine the strengths of GS-DiT and 4DGS-Cache?',
    a: '가능한 가설 — DiT prior로 변위 분포를 distill하되, baking 단계에서 4DGS-Cache의 triplane 구조를 사용. 즉 GS-DiT의 일반화 + 4DGS-Cache의 속도. 위험: triplane 메모리가 다시 문제. 완화: per-region adaptive bit allocation (NeRF-Triplane Compression 인용). 1티어 fit: SIGGRAPH > CVPR.',
    aEn: 'Hypothesis — distill the displacement distribution with the DiT prior, but use 4DGS-Cache\'s triplane structure at the baking step. So GS-DiT generalization + 4DGS-Cache speed. Risk: triplane memory returns. Mitigation: per-region adaptive bit allocation (cite NeRF-Triplane Compression). Tier-1 fit: SIGGRAPH > CVPR.',
    sources: 3,
    follow: ['이 가설을 검증하려면 어떤 ablation이 필요한가?'],
  },
  {
    id: 'q11',
    paperId: 'p1',
    cat: 'dataset',
    when: '1주 전',
    whenEn: '1w ago',
    q: 'D-NeRF 외에 GS-DiT가 잘 동작할 dataset 추측?',
    qEn: 'Beyond D-NeRF, where else would GS-DiT likely work?',
    a: 'HyperNeRF (논문에서 평가 누락) — 캐주얼 모노 캡처에서 변위 통계가 D-NeRF와 유사하므로 가능성 높음. 단 lighting variation이 더 큼 → DiT prior가 lighting까지 baking된 만큼 generalization 위험. NeRF-DS는 specularity가 dominant → prior가 무너질 가능성 높음. 우리 랙 데이터는 NeRF-DS에 더 가까움 — 주의.',
    aEn: 'HyperNeRF (missing from the paper) — casual monocular capture has similar displacement statistics to D-NeRF, so likely fine. Lighting variation is larger though → if the DiT prior bakes in lighting, generalization risk. NeRF-DS is specularity-dominated → high chance the prior breaks. Our rack data is closer to NeRF-DS — be careful.',
    sources: 2,
    follow: [],
  },
  {
    id: 'q12',
    paperId: 'p5', // RoboFM-13B
    cat: 'compare',
    when: '1주 전',
    whenEn: '1w ago',
    q: 'RoboFM-13B vs OpenVLA-2를 공정하게 비교하려면?',
    qEn: 'How to compare RoboFM-13B and OpenVLA-2 fairly?',
    a: '세 가지 정규화 — (1) ALOHA 6-서브셋 분할로 통일 (RoboFM은 4 보고 → 6으로 재계산 시 0.652). (2) 사전학습 데이터 크기 통제 (RoboFM 4.2M vs OpenVLA-2 1.2M → 1.2M으로 RoboFM 재학습 결과 미공개). (3) 동일 backbone (LLaMA-2). 결론: 현재로선 OpenVLA-2 우위지만 사전학습 데이터 변수가 미통제.',
    aEn: 'Three normalizations — (1) Unify to ALOHA 6-subset (RoboFM reports 4 → recalc to 6 gives 0.652). (2) Control pretrain data size (RoboFM 4.2M vs OpenVLA-2 1.2M; results at matched 1.2M not published). (3) Match backbone (LLaMA-2). Bottom line: OpenVLA-2 currently wins, but pretrain-data variable is uncontrolled.',
    sources: 3,
    follow: ['통제된 사전학습 크기로 다시 평가한 study가 있는가?'],
  },
];

export { QUESTIONS, QA_CATEGORIES };
